import React, {useEffect, useMemo, useRef, useState} from 'react';
import {createEditor, Descendant, Node} from 'slate';
import {withHistory} from 'slate-history';
import {Slate, Editable, withReact} from 'slate-react';

import {ElementRenderer} from '../renderers/ElementRenderer';
import {LeafRenderer} from '../renderers/LeafRenderer';
import {validateNode} from '../utils/nodeHelpers';

interface DocumentPreviewProps {
  fileId: string;
  variant?: 'grid' | 'list';
  document?: unknown[];
  watermark?: Record<string, unknown> | null;
  pageOrientation?: 'portrait' | 'landscape';
  pageMargins?: {top: number; right: number; bottom: number; left: number};
}

interface DocSettings {
  watermark: Record<string, unknown> | null;
  pageOrientation: 'portrait' | 'landscape';
  pageMargins: {top: number; right: number; bottom: number; left: number};
}

const DEFAULT_DOC_SETTINGS: DocSettings = {
  watermark: null,
  pageOrientation: 'portrait',
  pageMargins: {top: 0.5, right: 0.5, bottom: 0.5, left: 0.5}
};

const isContentEmpty = (nodes: Descendant[]): boolean => {
  if (!nodes.length) return true;

  const hasText = (node: Node): boolean => {
    if (Node.isText(node)) {
      return node.text.trim().length > 0;
    }

    if (!Array.isArray(node.children)) return false;

    return node.children.some(hasText);
  };

  return !nodes.some(hasText);
};

const extractFirstPage = (document: unknown[]): Descendant[] => {
  if (!document.length) return [];

  const first = document[0] as Descendant;
  const pageNodes =
    (first as {type?: string}).type === 'page' ? [first] : document;

  return pageNodes.map((n) => validateNode(n)) as Descendant[];
};

const PreviewSlate: React.FC<{
  fileId: string;
  content: Descendant[];
  docSettings: DocSettings;
}> = ({fileId, content, docSettings}) => {
  const editor = useMemo(() => {
    const instance = withHistory(withReact(createEditor()));
    instance.children = content;
    instance.selection = null;

    return instance;
  }, [fileId, content]);

  return (
    <div className="preview-scale-wrapper">
      <Slate editor={editor} initialValue={content}>
        <Editable
          readOnly
          style={{pointerEvents: 'none'}}
          renderElement={(props) => (
            <ElementRenderer
              {...props}
              watermark={docSettings.watermark}
              pageOrientation={docSettings.pageOrientation}
              pageMargins={docSettings.pageMargins}
            />
          )}
          renderLeaf={(props) => <LeafRenderer {...props} />}
        />
      </Slate>
    </div>
  );
};

const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  fileId,
  variant = 'grid',
  document: reportDocument,
  watermark,
  pageOrientation,
  pageMargins
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const docSettings = useMemo<DocSettings>(
    () => ({
      watermark: watermark ?? null,
      pageOrientation: pageOrientation ?? 'portrait',
      pageMargins: pageMargins ?? DEFAULT_DOC_SETTINGS.pageMargins
    }),
    [watermark, pageOrientation, pageMargins]
  );

  const {content, isEmpty} = useMemo(() => {
    if (!Array.isArray(reportDocument) || reportDocument.length === 0) {
      return {content: null, isEmpty: true};
    }

    const firstPage = extractFirstPage(reportDocument);
    const empty = isContentEmpty(firstPage);

    return {
      content: empty ? null : firstPage,
      isEmpty: empty
    };
  }, [reportDocument]);

  useEffect(() => {
    setIsVisible(false);
  }, [fileId]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || isVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {rootMargin: '100px'}
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [fileId, isVisible]);

  const containerClass = `document-preview-container document-preview-container--${variant}`;

  if (!isVisible) {
    return (
      <div ref={containerRef} className={containerClass}>
        <div className="preview-loading" />
      </div>
    );
  }

  if (isEmpty || !content?.length) {
    return (
      <div ref={containerRef} className={containerClass}>
        <div className="preview-empty">
          <span className="preview-empty__icon" aria-hidden>
            📄
          </span>
          <span className="preview-empty__label">Blank document</span>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={containerClass}>
      <PreviewSlate
        fileId={fileId}
        content={content}
        docSettings={docSettings}
      />
    </div>
  );
};

export default DocumentPreview;
